// src/components/pages/HRDocumentation.jsx
// Ficha del colaborador (RRHH). Reúne en un solo sitio lo que antes estaba
// repartido: datos personales, condiciones laborales (sueldo, horario,
// refrigerio, planilla), datos bancarios y adjuntos. El colaborador edita lo
// suyo; el admin ve a todos y además configura las condiciones.
import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { format } from 'date-fns';
import { db, storage } from '../../services/firebase';
import {
    FiArrowLeft, FiUpload, FiFileText, FiCheckCircle, FiUser, FiSave,
    FiExternalLink, FiSearch, FiX, FiCreditCard, FiBriefcase, FiCamera, FiGift,
    FiClock, FiDollarSign, FiCoffee, FiAlertTriangle, FiSlash,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import Time12Input from '../ui/Time12Input';
import { saveEmployeeSettings } from '../../services/overtimeService';
import {
    DEFAULT_ENTRY, DEFAULT_EXIT, WORKDAY_TYPES, toNumber,
    getBreakMinutes, effectiveDailyHours, computeHourlyRate,
    isInPayroll, isInstructorOnly, isActiveUser, calculateHours,
} from '../../services/payrollCalc';

const EMPTY = {
    nombresCompletos: '', dni: '', profesion: '', descripcion: '', ruc: '',
    fechaNacimiento: '',
    banco: '', numeroCuenta: '', cci: '',
    cvUrl: '', cvName: '', pagoUrl: '', pagoName: '',
};

// Edad a partir de la fecha de nacimiento, para mostrarla junto al campo.
const calcAge = (iso) => {
    if (!iso) return null;
    const born = new Date(iso + 'T00:00:00');
    if (isNaN(born.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - born.getFullYear();
    const m = today.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
    return age >= 0 && age < 120 ? age : null;
};

// Campo genérico con tipo, para reutilizar el mismo estilo en fechas.
const TypedField = ({ label, type = 'text', value, onChange, disabled, hint }) => (
    <div>
        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
        <input type={type} value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-400 outline-none disabled:bg-gray-50 disabled:text-gray-500" />
        {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
);

const isComplete = (d = {}) => !!(d.nombresCompletos && d.dni && (d.cvUrl || d.numeroCuenta));

const Field = ({ label, value, onChange, placeholder, disabled, textarea }) => (
    <div>
        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
        {textarea ? (
            <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-400 outline-none resize-none disabled:bg-gray-50 disabled:text-gray-500" />
        ) : (
            <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-400 outline-none disabled:bg-gray-50 disabled:text-gray-500" />
        )}
    </div>
);

const FileBox = ({ label, current, currentName, file, onPick, accept, disabled }) => (
    <div>
        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
        <div className="flex items-center gap-2">
            <label className={`flex-1 border-2 border-dashed rounded-xl p-3 flex items-center gap-2 transition ${disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300 hover:bg-gray-50 cursor-pointer'}`}>
                <input type="file" accept={accept} disabled={disabled} className="hidden" onChange={e => onPick(e.target.files[0] || null)} />
                <FiUpload className="text-gray-400 shrink-0" size={16} />
                <span className="text-xs text-gray-600 truncate">{file ? file.name : (currentName || 'Subir archivo')}</span>
            </label>
            {current && (
                <a href={current} target="_blank" rel="noopener noreferrer"
                    className="shrink-0 px-2.5 py-2 bg-sky-50 text-sky-700 rounded-xl text-xs font-bold hover:bg-sky-100 flex items-center gap-1" title="Ver archivo actual">
                    <FiExternalLink size={13} /> Ver
                </a>
            )}
        </div>
    </div>
);

// ── Condiciones laborales (sólo admin) ─────────────────────────────────────
// Es la configuración que antes vivía en Ajustes > lista de colaboradores.
// Aquí queda junto al resto de la ficha de la persona.
const ConditionsCard = ({ user, settings, globalSettings, onSaved }) => {
    const [form, setForm] = useState({
        hourlyRate: '', monthlySalary: '',
        scheduledEntry: DEFAULT_ENTRY, scheduledExit: DEFAULT_EXIT,
        hasBreak: true, breakMinutes: '',
        workdayType: 'auto', customDailyHours: '',
        scheduleMode: 'fixed',
        inPayroll: true,
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const s = settings || {};
        setForm({
            hourlyRate: s.hourlyRate ? String(s.hourlyRate) : '',
            monthlySalary: s.monthlySalary ? String(s.monthlySalary) : '',
            scheduledEntry: s.scheduledEntry || DEFAULT_ENTRY,
            scheduledExit: s.scheduledExit || DEFAULT_EXIT,
            hasBreak: s.hasBreak !== false,
            breakMinutes: typeof s.breakMinutes === 'number' ? String(s.breakMinutes) : '',
            workdayType: s.workdayType || 'auto',
            customDailyHours: typeof s.customDailyHours === 'number' ? String(s.customDailyHours) : '',
            scheduleMode: s.scheduleMode === 'flexible' ? 'flexible' : 'fixed',
            inPayroll: isInPayroll(user, s),
        });
    }, [settings, user]);

    const draft = {
        scheduledEntry: form.scheduledEntry, scheduledExit: form.scheduledExit,
        hasBreak: form.hasBreak, breakMinutes: form.breakMinutes,
        workdayType: form.workdayType, customDailyHours: form.customDailyHours,
    };
    const breakMin = getBreakMinutes(draft, globalSettings);
    const dailyHrs = effectiveDailyHours(draft, globalSettings);
    const salaryVal = parseFloat(form.monthlySalary) || 0;
    const suggested = computeHourlyRate(salaryVal, dailyHrs);
    const typedRate = parseFloat(form.hourlyRate);
    const rateMismatch = suggested > 0 && !isNaN(typedRate) && Math.abs(typedRate - suggested) > 0.05;
    const minWage = Number(globalSettings?.minimumWage) || 0;
    const belowMin = salaryVal > 0 && minWage > 0 && salaryVal < minWage;

    const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

    // Horario irregular: no hay entrada/salida fija de la cual derivar las
    // horas del día, así que "Según horario" (auto) no tiene de dónde sacar
    // el número — se fuerza a "Personalizada" con las horas que ya tenía.
    const setScheduleMode = (mode) => {
        if (mode === 'flexible' && form.workdayType === 'auto') {
            set({ scheduleMode: mode, workdayType: 'custom', customDailyHours: form.customDailyHours || String(dailyHrs || 6) });
        } else {
            set({ scheduleMode: mode });
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const customHrs = toNumber(form.customDailyHours);
            const rate = (!isNaN(typedRate) && typedRate > 0) ? typedRate : computeHourlyRate(salaryVal, dailyHrs);
            await saveEmployeeSettings(user.id, {
                hourlyRate: rate,
                monthlySalary: salaryVal,
                hasBreak: !!form.hasBreak,
                breakMinutes: form.hasBreak ? breakMin : 0,
                workdayType: form.workdayType || 'auto',
                customDailyHours: (form.workdayType === 'custom' && customHrs !== null) ? customHrs : null,
                scheduledEntry: form.scheduledEntry,
                scheduledExit: form.scheduledExit,
                scheduleMode: form.scheduleMode === 'flexible' ? 'flexible' : 'fixed',
                workHoursPerDay: dailyHrs > 0 ? dailyHrs : 8,
                inPayroll: !!form.inPayroll,
            });
            set({ hourlyRate: String(rate) });
            toast.success('Condiciones guardadas');
            onSaved?.();
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar las condiciones');
        }
        setSaving(false);
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <FiClock className="text-sky-600" size={15} /> Condiciones laborales
                </h3>
                <label className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border cursor-pointer select-none transition-colors ${form.inPayroll ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                    <input type="checkbox" checked={form.inPayroll} onChange={e => set({ inPayroll: e.target.checked })}
                        className="w-3.5 h-3.5 accent-emerald-600 cursor-pointer" />
                    <span className={`text-[12px] font-bold ${form.inPayroll ? 'text-emerald-700' : 'text-gray-500'}`}>
                        {form.inPayroll ? 'En planilla' : 'Fuera de planilla'}
                    </span>
                </label>
            </div>

            {user?.isInstructor && (
                <p className="text-[11.5px] text-violet-700 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2">
                    Esta persona está marcada como <b>instructor</b>. Por defecto los instructores quedan fuera de planilla,
                    pero puedes incluirla aquí si también es personal de la empresa.
                </p>
            )}

            {!form.inPayroll ? (
                <p className="text-[12.5px] text-gray-500 flex items-center gap-2">
                    <FiSlash size={13} className="text-gray-400" />
                    Fuera de planilla: no marca asistencia, no se le detectan faltas y no entra en la nómina.
                </p>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Sueldo mensual</label>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500">S/</span>
                                <input type="number" step="50" value={form.monthlySalary} onChange={e => set({ monthlySalary: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200 ${belowMin ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200'}`} />
                            </div>
                            {belowMin && <p className="text-[11px] text-red-600 mt-1">Por debajo del mínimo (S/ {minWage})</p>}
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Tarifa por hora</label>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500">S/</span>
                                <input type="number" step="0.10" value={form.hourlyRate} placeholder={suggested ? String(suggested) : ''}
                                    onChange={e => set({ hourlyRate: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200" />
                            </div>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {suggested > 0 ? <>Sugerida: <b className="text-gray-600">S/ {suggested.toFixed(2)}</b> (sueldo ÷ 30 días ÷ {dailyHrs}h)</> : 'Se calcula sola desde el sueldo.'}
                                {rateMismatch && (
                                    <button onClick={() => set({ hourlyRate: String(suggested) })}
                                        className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold hover:bg-amber-100">
                                        Usar S/ {suggested.toFixed(2)}
                                    </button>
                                )}
                            </p>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide">Horario</label>
                            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                                <button type="button" onClick={() => setScheduleMode('fixed')}
                                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${form.scheduleMode !== 'flexible' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                                    Fijo
                                </button>
                                <button type="button" onClick={() => setScheduleMode('flexible')}
                                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${form.scheduleMode === 'flexible' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                                    Irregular
                                </button>
                            </div>
                        </div>
                        {form.scheduleMode === 'flexible' ? (
                            <p className="text-[12px] text-gray-500 bg-indigo-50/60 border border-indigo-100 rounded-xl px-3 py-2">
                                Puede marcar entrada y salida a la hora que quiera. No se le mide tardanza ni salida
                                temprana — solo se descuenta o se paga extra según si completa sus horas del día
                                (abajo, en Jornada).
                            </p>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <Time12Input value={form.scheduledEntry} onChange={(v) => set({ scheduledEntry: v })} />
                                <span className="text-gray-400 text-xs">a</span>
                                <Time12Input value={form.scheduledExit} onChange={(v) => set({ scheduledExit: v })} />
                            </div>
                        )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Jornada</label>
                            <div className="flex items-center gap-2">
                                <select value={form.workdayType} onChange={e => set({ workdayType: e.target.value })}
                                    className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer">
                                    {WORKDAY_TYPES.filter(t => t.id !== 'auto' || form.scheduleMode !== 'flexible').map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                </select>
                                {form.workdayType === 'custom' && (
                                    <>
                                        <input type="number" min="0" step="0.5" placeholder="8" value={form.customDailyHours}
                                            onChange={e => set({ customDailyHours: e.target.value })}
                                            className="w-20 px-2 py-2 border border-gray-200 rounded-xl text-sm text-right outline-none focus:ring-2 focus:ring-indigo-200" />
                                        <span className="text-xs text-gray-400">h/día</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Refrigerio</label>
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                    <input type="checkbox" checked={form.hasBreak} onChange={e => set({ hasBreak: e.target.checked })}
                                        className="w-3.5 h-3.5 accent-amber-500 cursor-pointer" />
                                    <span className="text-[12.5px] text-gray-600 font-medium">Tiene</span>
                                </label>
                                {form.hasBreak ? (
                                    <>
                                        <input type="number" min="0" step="15" value={form.breakMinutes}
                                            placeholder={String(getBreakMinutes(null, globalSettings))}
                                            onChange={e => set({ breakMinutes: e.target.value })}
                                            className="w-20 px-2 py-2 border border-gray-200 rounded-xl text-sm text-right outline-none focus:ring-2 focus:ring-amber-200" />
                                        <span className="text-xs text-gray-400">min · no se paga</span>
                                    </>
                                ) : (
                                    <span className="text-xs text-gray-400">Cobra la jornada completa.</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 text-[12px] text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                        <FiCoffee size={13} className="text-gray-400 shrink-0" />
                        Jornada efectiva: <b className="text-gray-700 font-mono">{dailyHrs}h</b>
                        <span className="text-gray-400">
                            ({form.workdayType === 'auto'
                                ? `${calculateHours(form.scheduledEntry, form.scheduledExit)}h de horario menos ${breakMin} min de refrigerio`
                                : `jornada ${(WORKDAY_TYPES.find(t => t.id === form.workdayType)?.label || '').toLowerCase()}`})
                        </span>
                    </div>
                </>
            )}

            <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 bg-sky-600 text-white rounded-xl text-[13px] font-bold hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2">
                    {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSave size={14} />}
                    Guardar condiciones
                </button>
            </div>
        </div>
    );
};

const HRDocumentation = ({
    currentUser, isAdmin, users = [], onClose,
    employeeSettings = {}, globalSettings = {}, attendanceRecords = [],
}) => {
    const [targetId, setTargetId] = useState(isAdmin ? null : currentUser?.uid);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cvFile, setCvFile] = useState(null);
    const [pagoFile, setPagoFile] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [search, setSearch] = useState('');

    // Resumen del mes en curso por persona: lo que se ve en las tarjetas.
    const monthStats = useMemo(() => {
        const month = format(new Date(), 'yyyy-MM');
        const acc = {};
        for (const r of attendanceRecords) {
            if (!r.date?.startsWith(month)) continue;
            const cur = acc[r.userId] || { lateMin: 0, lateCount: 0, faltas: 0, breakExcess: 0, days: 0 };
            cur.lateMin += r.lateMinutes || 0;
            if ((r.lateMinutes || 0) > 0) cur.lateCount++;
            if (r.status === 'falta') cur.faltas++;
            cur.breakExcess += r.breakExcessMinutes || 0;
            cur.days++;
            acc[r.userId] = cur;
        }
        return acc;
    }, [attendanceRecords]);

    const canEdit = isAdmin || targetId === currentUser?.uid;
    const targetUser = users.find(u => u.id === targetId) || (targetId === currentUser?.uid ? currentUser : null);

    useEffect(() => {
        if (!targetId) { setForm(EMPTY); return; }
        setLoading(true);
        setCvFile(null); setPagoFile(null); setPhotoFile(null); setPhotoPreview('');
        getDoc(doc(db, 'users', targetId))
            .then(snap => {
                const data = snap.exists() ? snap.data() : {};
                const d = data.hrDoc || {};
                setForm({ ...EMPTY, ...d, nombresCompletos: d.nombresCompletos || data.displayName || '' });
            })
            .catch(() => setForm(EMPTY))
            .finally(() => setLoading(false));
    }, [targetId]);

    const set = (k) => (v) => setForm(prev => ({ ...prev, [k]: v }));

    const upload = async (file, kind) => {
        const ext = (file.name.split('.').pop() || 'dat').toLowerCase();
        const r = ref(storage, `hr_docs/${targetId}/${kind}-${Date.now()}.${ext}`);
        await uploadBytes(r, file);
        return { url: await getDownloadURL(r), name: file.name };
    };

    // La foto va a hr_docs/{uid}/, no a users/{uid}/, porque en Storage esa
    // segunda ruta sólo la puede escribir el propio dueño y aquí el admin
    // también edita las fichas de los demás.
    const pickPhoto = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Elige una imagen'); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error('La imagen supera los 5 MB'); return; }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const handleSavePhoto = async () => {
        if (!photoFile || !targetId || !canEdit) return;
        setUploadingPhoto(true);
        try {
            const ext = (photoFile.name.split('.').pop() || 'jpg').toLowerCase();
            const r = ref(storage, `hr_docs/${targetId}/profile-${Date.now()}.${ext}`);
            await uploadBytes(r, photoFile);
            const url = await getDownloadURL(r);
            // photoURL en Firestore: es de donde la app lee el avatar en todas partes.
            await updateDoc(doc(db, 'users', targetId), { photoURL: url });
            setPhotoFile(null);
            setPhotoPreview(url);
            toast.success('Foto de perfil actualizada');
        } catch (e) {
            console.error(e);
            toast.error('Error al subir la foto: ' + (e.code === 'storage/unauthorized' ? 'sin permiso' : e.message));
        }
        setUploadingPhoto(false);
    };

    const handleSave = async () => {
        if (!targetId || !canEdit) return;
        setSaving(true);
        try {
            const data = { ...form };
            if (cvFile) { const u = await upload(cvFile, 'cv'); data.cvUrl = u.url; data.cvName = u.name; }
            if (pagoFile) { const u = await upload(pagoFile, 'pago'); data.pagoUrl = u.url; data.pagoName = u.name; }
            await updateDoc(doc(db, 'users', targetId), { hrDoc: { ...data, updatedAt: new Date().toISOString() } });
            setForm(data); setCvFile(null); setPagoFile(null);
            toast.success('Documentación guardada');
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar: ' + (e.code === 'storage/unauthorized' ? 'sin permiso para subir' : e.message));
        }
        setSaving(false);
    };

    // ── Vista admin: lista de colaboradores ──────────────────────────────────
    if (isAdmin && !targetId) {
        // Se listan todos, incluidos los de baja: aquí se consultan fichas
        // antiguas. Su estado va etiquetado en la tarjeta.
        const list = users
            .filter(u => !search || `${u.displayName || ''} ${u.email || ''}`.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
        return (
            <div className="min-h-full bg-gray-50 p-6 lg:p-10">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2 font-semibold">
                            <FiArrowLeft size={13} /> Volver a Control de Personal
                        </button>
                        <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2"><FiUser className="text-sky-600" /> Recursos Humanos · Documentación</h1>
                        <p className="text-sm text-gray-500 mt-1">Datos y archivos de cada colaborador. Haz clic en uno para ver o editar.</p>
                    </div>
                    <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar colaborador..."
                            className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-400 outline-none w-64" />
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map(u => {
                        const done = isComplete(u.hrDoc);
                        const st = employeeSettings[u.id] || {};
                        const inPayroll = isInPayroll(u, st);
                        const stats = monthStats[u.id];
                        const dailyHrs = effectiveDailyHours(st, globalSettings);
                        return (
                            <button key={u.id} onClick={() => setTargetId(u.id)}
                                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-left hover:border-sky-300 hover:shadow transition">
                                <div className="flex items-center gap-3">
                                    {u.photoURL
                                        ? <img src={u.photoURL} className="w-11 h-11 rounded-full object-cover shrink-0" alt="" />
                                        : <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center text-white font-extrabold shrink-0">{(u.displayName || u.email || '?').charAt(0).toUpperCase()}</div>}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-gray-800 truncate">{u.displayName || u.email?.split('@')[0]}</p>
                                        <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
                                            {u.hrDoc?.fechaNacimiento && <FiGift size={10} className="text-pink-400 shrink-0" />}
                                            {u.hrDoc?.profesion || u.email}
                                        </p>
                                    </div>
                                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 flex items-center gap-1 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {done ? <><FiCheckCircle size={11} /> Completo</> : 'Pendiente'}
                                    </span>
                                </div>

                                {/* Etiquetas de situación */}
                                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                                    {inPayroll ? (
                                        <span className="text-[9.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Planilla</span>
                                    ) : (
                                        <span className="text-[9.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Fuera de planilla</span>
                                    )}
                                    {u.isInstructor && (
                                        <span className="text-[9.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">Instructor</span>
                                    )}
                                    {!isActiveUser(u) && (
                                        <span className="text-[9.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">{u.status || 'inactivo'}</span>
                                    )}
                                </div>

                                {/* Condiciones y mes en curso */}
                                {inPayroll && (
                                    <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2">
                                        <div>
                                            <div className="text-[9px] uppercase tracking-wide text-gray-400 font-bold flex items-center gap-1"><FiDollarSign size={9} /> Sueldo</div>
                                            <div className="text-[13px] font-extrabold text-gray-800 tabular-nums mt-0.5">
                                                {st.monthlySalary ? `S/ ${st.monthlySalary}` : <span className="text-gray-300 font-medium">sin definir</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] uppercase tracking-wide text-gray-400 font-bold flex items-center gap-1"><FiClock size={9} /> Jornada</div>
                                            <div className="text-[13px] font-extrabold text-gray-800 tabular-nums mt-0.5">{dailyHrs}h</div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] uppercase tracking-wide text-gray-400 font-bold flex items-center gap-1"><FiAlertTriangle size={9} /> Este mes</div>
                                            <div className="text-[13px] font-extrabold tabular-nums mt-0.5">
                                                {stats ? (
                                                    <span className={stats.lateMin > 0 || stats.faltas > 0 ? 'text-orange-600' : 'text-emerald-600'}>
                                                        {stats.lateMin > 0 ? `${stats.lateMin}m` : 'OK'}
                                                        {stats.faltas > 0 && <span className="text-red-600"> · {stats.faltas}F</span>}
                                                    </span>
                                                ) : <span className="text-gray-300 font-medium">—</span>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                    {list.length === 0 && <p className="text-sm text-gray-400 col-span-full text-center py-10">Sin colaboradores</p>}
                </div>
            </div>
        );
    }

    // ── Editor (colaborador propio o admin sobre un colaborador) ──────────────
    return (
        <div className="min-h-full bg-gray-50 p-6 lg:p-10">
            <div className="max-w-3xl mx-auto">
                <button onClick={() => (isAdmin ? setTargetId(null) : onClose())}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3 font-semibold">
                    <FiArrowLeft size={13} /> {isAdmin ? 'Volver a la lista' : 'Volver a Control de Personal'}
                </button>

                <div className="flex items-center gap-4 mb-6">
                    <div className="relative shrink-0">
                        {(photoPreview || targetUser?.photoURL)
                            ? <img src={photoPreview || targetUser.photoURL} className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow" alt="" />
                            : <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center text-white font-extrabold text-xl">{(targetUser?.displayName || targetUser?.email || form.nombresCompletos || '?').charAt(0).toUpperCase()}</div>}
                        {canEdit && (
                            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center cursor-pointer shadow-md transition"
                                title="Cambiar foto de perfil">
                                <input type="file" accept="image/*" className="hidden" onChange={e => pickPhoto(e.target.files[0])} />
                                <FiCamera size={13} />
                            </label>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl font-extrabold text-gray-900 truncate">{isAdmin ? (targetUser?.displayName || targetUser?.email) : 'Mi documentación'}</h1>
                        <p className="text-sm text-gray-500">Completa tus datos y adjunta tu CV y medio de pago.</p>
                        {photoFile && (
                            <div className="flex items-center gap-2 mt-2">
                                <button onClick={handleSavePhoto} disabled={uploadingPhoto}
                                    className="px-3 py-1.5 bg-sky-600 text-white rounded-lg text-xs font-bold hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5">
                                    {uploadingPhoto ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiUpload size={12} />}
                                    Guardar foto
                                </button>
                                <button onClick={() => { setPhotoFile(null); setPhotoPreview(''); }}
                                    className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700">Cancelar</button>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="py-16 text-center"><div className="w-7 h-7 border-2 border-sky-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : (
                    <div className="space-y-5">
                        {/* Datos personales */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><FiUser className="text-sky-600" size={15} /> Datos personales</h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Nombres completos" value={form.nombresCompletos} onChange={set('nombresCompletos')} placeholder="Nombre y apellidos" disabled={!canEdit} />
                                <Field label="DNI" value={form.dni} onChange={set('dni')} placeholder="Documento de identidad" disabled={!canEdit} />
                                <Field label="Profesión" value={form.profesion} onChange={set('profesion')} placeholder="Ej. Ingeniero Civil" disabled={!canEdit} />
                                <Field label="RUC" value={form.ruc} onChange={set('ruc')} placeholder="RUC (si tienes)" disabled={!canEdit} />
                                <TypedField label="Fecha de nacimiento" type="date" value={form.fechaNacimiento} onChange={set('fechaNacimiento')}
                                    disabled={!canEdit}
                                    hint={calcAge(form.fechaNacimiento) !== null ? `${calcAge(form.fechaNacimiento)} años` : undefined} />
                            </div>
                            <Field label="Descripción" value={form.descripcion} onChange={set('descripcion')} placeholder="Una breve descripción profesional..." disabled={!canEdit} textarea />
                        </div>

                        {/* Condiciones laborales (sólo admin) */}
                        {isAdmin && targetUser && (
                            <ConditionsCard
                                user={targetUser}
                                settings={employeeSettings[targetId]}
                                globalSettings={globalSettings}
                            />
                        )}

                        {/* Datos bancarios */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><FiCreditCard className="text-sky-600" size={15} /> Medio de pago</h3>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <Field label="Banco" value={form.banco} onChange={set('banco')} placeholder="Ej. BCP" disabled={!canEdit} />
                                <Field label="N° de cuenta" value={form.numeroCuenta} onChange={set('numeroCuenta')} placeholder="Número de cuenta" disabled={!canEdit} />
                                <Field label="CCI" value={form.cci} onChange={set('cci')} placeholder="Código interbancario" disabled={!canEdit} />
                            </div>
                            <FileBox label="Comprobante / estado de cuenta (PDF o imagen)" current={form.pagoUrl} currentName={form.pagoName}
                                file={pagoFile} onPick={setPagoFile} accept="image/*,application/pdf" disabled={!canEdit} />
                        </div>

                        {/* CV */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><FiBriefcase className="text-sky-600" size={15} /> Currículum (CV)</h3>
                            <FileBox label="Adjuntar CV (PDF)" current={form.cvUrl} currentName={form.cvName}
                                file={cvFile} onPick={setCvFile} accept="application/pdf" disabled={!canEdit} />
                        </div>

                        {canEdit && (
                            <div className="flex justify-end">
                                <button onClick={handleSave} disabled={saving}
                                    className="px-5 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-bold hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2 shadow-sm">
                                    {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSave size={15} />}
                                    Guardar documentación
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HRDocumentation;
